import { useEffect, useRef } from "react";
import { loadToken } from "src/helpers/auth";

/**
 * Hook to handle automatic page refresh when company switch occurs
 * This prevents issues where other tabs use the wrong company's token
 */
export default function useCompanySwitchRefresh() {
  const currentTokenRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const lastRefreshTimeRef = useRef(0);
  const ACCESS_TOKEN_KEY = "bf-token";

  useEffect(() => {
    // Initialize current token
    currentTokenRef.current = loadToken();

    // Function to handle storage changes
    const handleStorageChange = (event) => {
      // Only listen for token changes
      if (event.key !== ACCESS_TOKEN_KEY) return;

      const newTokenData = event.newValue;
      const oldTokenData = event.oldValue;

      // Skip if no token change or if we're already refreshing
      if (newTokenData === oldTokenData || isRefreshingRef.current) return;

      let newToken, oldToken;

      try {
        // Parse new token data
        const newParsed = newTokenData ? JSON.parse(newTokenData) : null;
        newToken = newParsed?.token || newTokenData;

        // Parse old token data
        const oldParsed = oldTokenData ? JSON.parse(oldTokenData) : null;
        oldToken = oldParsed?.token || oldTokenData;
      } catch {
        // Fallback to raw values if parsing fails
        newToken = newTokenData;
        oldToken = oldTokenData;
      }

      // Skip if no actual token change
      if (newToken === oldToken) return;

      // Skip if this is the tab that initiated the change
      if (newToken === currentTokenRef.current) return;

      // Prevent multiple refreshes within a short time window (5 seconds)
      const now = Date.now();
      if (now - lastRefreshTimeRef.current < 5000) {
        return;
      }

      // Mark that we're refreshing to prevent infinite loops
      isRefreshingRef.current = true;
      lastRefreshTimeRef.current = now;

      // Refresh the page after a short delay to ensure the new token is properly set
      setTimeout(() => {
        try {
          window.location.reload();
        } catch (error) {
          console.error("Failed to refresh page:", error);
          // Reset the flag if refresh fails
          isRefreshingRef.current = false;
        }
      }, 100);
    };

    // Listen for storage events (changes from other tabs)
    window.addEventListener("storage", handleStorageChange);

    // Also listen for direct localStorage changes within the same tab
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      if (key === ACCESS_TOKEN_KEY) {
        const oldValue = localStorage.getItem(key);
        if (oldValue !== value) {
          // Dispatch a custom event to simulate storage event for same tab
          try {
            window.dispatchEvent(
              new StorageEvent("storage", {
                key,
                newValue: value,
                oldValue,
                storageArea: localStorage
              })
            );
          } catch (error) {
            // Silent fail for storage event dispatch
          }
        }
      }
      return originalSetItem.apply(this, arguments);
    };

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      // Restore original setItem
      localStorage.setItem = originalSetItem;
    };
  }, []);

  // Update current token reference when it changes
  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const currentToken = loadToken();
        if (currentToken !== currentTokenRef.current) {
          currentTokenRef.current = currentToken;
        }
      } catch (error) {
        // Silent fail for token reference update
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);
}
